/**
 * Loading Indicator Utility
 * Provides a consistent loading experience across the application
 * Uses standardized .loading-message and .loading-spinner classes
 *
 * DESIGN DECISION: Spinner only (no text) for cleaner, language-independent UI
 * The spinning animation is universally understood across all cultures
 */

import { t } from '../i18n/config.js';

// Track the full-page loader element
let _pageLoaderElement = null;

/**
 * Show a full-page centered loader.
 * Blurs and fades the page content behind a fixed overlay with a spinner in the center.
 * Only one page loader can be active at a time.
 * @returns {void}
 */
export function showPageLoader() {
  // Don't create duplicates
  if (_pageLoaderElement) return;

  const backdrop = document.createElement('div');
  backdrop.className = 'page-loader-backdrop';
  backdrop.innerHTML = '<div class="loading-spinner"></div>';
  document.body.appendChild(backdrop);

  // Trigger transition on next frame
  requestAnimationFrame(() => {
    backdrop.classList.add('active');
  });

  _pageLoaderElement = backdrop;
}

/**
 * Hide the full-page centered loader.
 * Fades out the overlay and removes it from the DOM.
 * @returns {void}
 */
export function hidePageLoader() {
  if (!_pageLoaderElement) return;

  _pageLoaderElement.classList.remove('active');

  // Remove from DOM after fade-out transition
  const el = _pageLoaderElement;
  _pageLoaderElement = null;

  setTimeout(() => {
    if (el.parentNode) el.remove();
  }, 220); // slightly longer than the 0.2s CSS transition
}

/**
 * Show loading indicator in a container (replaces content)
 * @param {HTMLElement|string} container - Container element or selector
 * @param {boolean} showText - Whether to show loading text (default: false for spinner only)
 * @returns {void}
 */
export function showLoading(container, showText = false) {
    const element = typeof container === 'string'
        ? document.querySelector(container)
        : container;

    if (!element) {
        console.warn('Loading indicator: Container not found');
        return;
    }

    if (showText) {
        element.innerHTML = `
      <div class="loading-message">
        <div class="loading-spinner"></div>
        <span>${t('app.loading')}</span>
      </div>
    `;
    } else {
        // Spinner only - no text for cleaner UI
        element.innerHTML = `
      <div class="loading-message">
        <div class="loading-spinner"></div>
      </div>
    `;
    }
}

/**
 * Show loading overlay on top of existing content.
 * Uses the full-page centered loader (blurs page behind) for a consistent experience.
 * @param {HTMLElement|string} container - Container element or selector (kept for API compatibility)
 * @param {boolean} showText - Whether to show loading text (default: false for spinner only)
 * @returns {HTMLElement} The overlay element (for manual removal if needed)
 */
export function showLoadingOverlay(_container, _showText = false) {
  // Use the full-page loader for a consistent centered experience
  showPageLoader();
  return _pageLoaderElement;
}

/**
 * Hide loading overlay
 * @param {HTMLElement|string} container - Container element or selector (kept for API compatibility)
 * @returns {void}
 */
export function hideLoadingOverlay(_container) {
  hidePageLoader();
}

/**
 * Show inline loading spinner (small, for buttons or inline elements)
 * @param {HTMLElement|string} container - Container element or selector
 * @returns {void}
 */
export function showInlineLoading(container) {
    const element = typeof container === 'string'
        ? document.querySelector(container)
        : container;

    if (!element) {
        console.warn('Inline loading: Container not found');
        return;
    }

    const spinner = document.createElement('div');
    spinner.className = 'loading-spinner-inline';
    element.appendChild(spinner);
}

/**
 * Hide inline loading spinner
 * @param {HTMLElement|string} container - Container element or selector
 * @returns {void}
 */
export function hideInlineLoading(container) {
    const element = typeof container === 'string'
        ? document.querySelector(container)
        : container;

    if (!element) {
        return;
    }

    const spinner = element.querySelector('.loading-spinner-inline');
    if (spinner) {
        spinner.remove();
    }
}

/**
 * Show error message in container
 * @param {HTMLElement|string} container - Container element or selector
 * @param {string} message - Error message
 * @param {Function} retryCallback - Optional retry function
 * @returns {void}
 */
export function showError(container, message, retryCallback = null) {
    const element = typeof container === 'string'
        ? document.querySelector(container)
        : container;

    if (!element) {
        console.warn('Error message: Container not found');
        return;
    }

    const retryButton = retryCallback
        ? `<button class="admin-btn admin-btn-primary" onclick="(${retryCallback.toString()})()">
         ${t('app.retry')}
       </button>`
        : '';

    element.innerHTML = `
    <div class="error-message">
      <div class="error-icon">❌</div>
      <p>${message}</p>
      ${retryButton}
    </div>
  `;
}

/**
 * Show empty state message
 * @param {HTMLElement|string} container - Container element or selector
 * @param {string} message - Empty state message
 * @param {string} icon - Optional emoji icon
 * @returns {void}
 */
export function showEmptyState(container, message, icon = '📭') {
    const element = typeof container === 'string'
        ? document.querySelector(container)
        : container;

    if (!element) {
        console.warn('Empty state: Container not found');
        return;
    }

    element.innerHTML = `
    <div class="empty-state">
      <div class="empty-icon">${icon}</div>
      <p>${message}</p>
    </div>
  `;
}
